
import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import { db } from '../../services/firebaseService';
import { doc, onSnapshot, setDoc, increment } from 'firebase/firestore';

import { BRAND } from '../../constants/mapColors';
const COUNTRY_LABELS: { key: string; country: string; lat: number; lon: number; flag: string }[] = [
  { key: 'sverige',  country: 'Sverige',  lat: 63.0, lon: 16.5, flag: '🇸🇪' },
  { key: 'danmark',  country: 'Danmark',  lat: 56.0, lon: 10.5, flag: '🇩🇰' },
  { key: 'finland',  country: 'Finland',  lat: 64.5, lon: 26.0, flag: '🇫🇮' },
  { key: 'island',   country: 'Island',   lat: 65.0, lon: -19.0, flag: '🇮🇸' },
];

// Per-country polygons — only drawn when that country is DISABLED
const COUNTRY_POLYGONS: Record<string, [number, number][]> = {
  sverige: [
    [55.3, 12.6], [55.4, 14.2], [55.6, 15.2],
    [56.2, 15.8], [56.8, 16.3],
    [57.5, 17.2], [58.5, 17.2], [59.0, 18.1], [59.5, 18.5], [60.5, 18.5],
    [61.5, 17.5], [62.0, 18.0], [63.0, 19.5], [64.0, 22.0],
    [66.0, 24.2], [67.5, 24.0], [68.5, 22.0], [69.1, 20.5],
    [69.0, 18.5], [68.0, 18.0], [67.0, 16.0], [66.0, 15.5],
    [65.0, 14.5], [64.0, 14.0], [63.0, 12.0], [62.0, 12.0],
    [61.0, 12.5], [60.0, 12.5],
    [59.5, 11.2], [59.1, 10.9], [58.9, 10.8], [58.5, 10.9],
    [58.0, 11.0], [57.5, 11.4], [57.0, 11.6],
    [56.5, 12.0], [56.0, 12.4], [55.6, 12.7], [55.3, 12.6],
  ],
  danmark: [
    // Jutland + rough island coverage bounding box
    [54.5, 8.0], [54.5, 12.8], [55.2, 12.8], [55.4, 15.2],
    [56.0, 15.2], [56.2, 10.8], [57.8, 10.8], [57.8, 8.0], [54.5, 8.0],
  ],
  finland: [
    [59.8, 22.0], [60.0, 27.0], [60.5, 28.5], [61.5, 29.5],
    [62.5, 30.0], [63.5, 30.5], [64.5, 30.0], [65.5, 29.5],
    [66.5, 29.0], [68.0, 28.5], [69.0, 27.5], [70.0, 27.5],
    [70.1, 26.0], [69.5, 25.0], [68.8, 22.5], [68.5, 22.0],
    [67.5, 24.0], [66.0, 24.2], [64.0, 22.0], [63.0, 21.5],
    [61.5, 21.0], [60.3, 22.0], [59.8, 22.0],
  ],
  island: [
    [63.3, -24.5], [63.3, -13.5], [66.5, -13.5], [66.5, -24.5], [63.3, -24.5],
  ],
};

const COUNTERS_DOC = doc(db, 'system', 'country_requests');
const NORDIC_CONFIG_DOC = doc(db, 'system', 'nordic_config');

async function incrementCountryRequest(countryKey: string) {
  await setDoc(COUNTERS_DOC, { [countryKey]: increment(1) }, { merge: true });
}

interface NordicComingSoonOverlayProps {
  onRequestCountry?: (country: string) => void;
  enabledCountries?: Record<string, boolean>;
  isAdmin?: boolean;
}

const KEY_TO_COUNTRY: Record<string, string> = { sverige: 'sweden', danmark: 'denmark', island: 'iceland', finland: 'finland' };

const NordicComingSoonOverlay: React.FC<NordicComingSoonOverlayProps> = ({ onRequestCountry, enabledCountries: enabledCountriesProp, isAdmin }) => {
  const map = useMap();
  const { t, i18n } = useTranslation();
  const layerRef = useRef<L.LayerGroup | null>(null);
  const callbackRef = useRef(onRequestCountry);
  callbackRef.current = onRequestCountry;
  const [counts, setCounts] = useState<Record<string, number>>({});
  // Subscribe to nordic_config directly so overlay always reflects Firestore truth,
  // regardless of parent component loading state.
  const [liveConfig, setLiveConfig] = useState<Record<string, boolean> | null>(null);

  useEffect(() => {
    let mounted = true;
    const ALL_ENABLED = { sweden: true, denmark: true, finland: true, iceland: true };

    // Fetch config from same-origin JSON file — never blocked by ad-blockers.
    // Cache-bust with timestamp so stale service-worker or HTTP cache never serves old values.
    // Falls back to all-enabled if the file can't be reached.
    fetch(`/nordic_config.json?v=${Date.now()}`)
      .then(r => r.json())
      .then((data: Record<string, boolean>) => {
        if (!mounted) return;
        console.log('[NordicOverlay v3] config loaded:', JSON.stringify(data));
        setLiveConfig({
          sweden: data.sweden === true,
          denmark: data.denmark === true,
          finland: data.finland === true,
          iceland: data.iceland === true,
        });
      })
      .catch((err) => {
        console.log('[NordicOverlay v3] fetch failed, all enabled:', err);
        if (mounted) setLiveConfig(ALL_ENABLED);
      });
    return () => { mounted = false; };
  }, []);

  // Use live Firestore config once available, fall back to parent prop
  const enabledCountries = liveConfig ?? enabledCountriesProp;

  // Admin sees the map without any overlay
  const allEnabled = isAdmin || (enabledCountries && Object.values(KEY_TO_COUNTRY).every(c => enabledCountries[c]));

  // Subscribe to counters in Firestore
  useEffect(() => {
    if (allEnabled) return;
    const unsub = onSnapshot(COUNTERS_DOC, (snap) => {
      if (snap.exists()) {
        setCounts(snap.data() as Record<string, number>);
      }
    }, () => {});
    return unsub;
  }, [allEnabled]);

  // Build / rebuild layers when counts or enabled countries change
  useEffect(() => {
    if (layerRef.current) {
      layerRef.current.remove();
      layerRef.current = null;
    }

    // Wait for live Firestore config before drawing anything — avoids stale parent-prop flash
    if (allEnabled || liveConfig === null) return;

    const group = L.layerGroup();

    const visibleLabels = COUNTRY_LABELS.filter(c => !liveConfig[KEY_TO_COUNTRY[c.key]]);

    // Draw a simple filled polygon for each disabled country
    for (const { key } of visibleLabels) {
      const poly = COUNTRY_POLYGONS[key];
      if (poly) {
        const maskPolygon = L.polygon(poly, {
          color: 'transparent',
          fillColor: '#1a1a2e',
          fillOpacity: 0.25,
          interactive: false,
        });
        group.addLayer(maskPolygon);
      }
    }

    for (const { key, country, lat, lon, flag } of visibleLabels) {
      const btnId = `nordic-req-${country}`;
      const count = counts[key] || 0;
      const requestsWord = count === 1
        ? t('auto.map.nordiccomingsoonover.requests_singular', 'etterlysning')
        : t('auto.map.nordiccomingsoonover.requests_plural', 'etterlysninger');
      const countBadge = count > 0
        ? `<span style="
            font-size: 10px; font-weight: 800; color: #6366f1;
            background: rgba(255,255,255,0.9); padding: 1px 8px; border-radius: 6px;
            white-space: nowrap; pointer-events: none;
            box-shadow: 0 1px 3px rgba(0,0,0,0.08);
          ">${count} ${requestsWord}</span>`
        : '';

      const icon = L.divIcon({
        className: 'nordic-coming-soon-label',
        html: `<div style="
          display: flex; flex-direction: column; align-items: center; gap: 2px;
          user-select: none;
        ">
          <span style="font-size: 28px; pointer-events: none;">${flag}</span>
          <span style="
            font-size: 13px; font-weight: 800; color: #475569;
            text-transform: uppercase; letter-spacing: 2px;
            text-shadow: 0 1px 3px rgba(255,255,255,0.8);
            white-space: nowrap; pointer-events: none;
          ">${country}</span>
          <span style="
            font-size: 11px; font-weight: 700; color: ${BRAND};
            background: rgba(255,255,255,0.85); backdrop-filter: blur(4px);
            padding: 2px 10px; border-radius: 8px;
            white-space: nowrap; pointer-events: none;
            box-shadow: 0 1px 4px rgba(0,0,0,0.1);
          ">${t('auto.map.nordiccomingsoonover.kommer_snart', 'Kommer snart')}</span>
          ${countBadge}
          <button id="${btnId}" style="
            margin-top: 4px; padding: 4px 12px; border-radius: 10px;
            background: ${BRAND}; color: white; border: none;
            font-size: 10px; font-weight: 800; cursor: pointer;
            text-transform: uppercase; letter-spacing: 1px;
            box-shadow: 0 2px 6px rgba(16,185,129,0.4);
            transition: transform 0.15s, box-shadow 0.15s;
            pointer-events: auto;
          " onmouseover="this.style.transform='scale(1.05)';this.style.boxShadow='0 3px 10px rgba(16,185,129,0.5)'"
             onmouseout="this.style.transform='scale(1)';this.style.boxShadow='0 2px 6px rgba(16,185,129,0.4)'"
          >${t('auto.map.nordiccomingsoonover.etterlys_landet', 'Etterlys landet')}</button>
        </div>`,
        iconSize: [140, 100],
        iconAnchor: [70, 50],
      });

      const marker = L.marker([lat, lon], { icon, interactive: false, zIndexOffset: -1000 });
      group.addLayer(marker);
    }

    group.addTo(map);
    layerRef.current = group;

    return () => {
      if (layerRef.current) {
        layerRef.current.remove();
        layerRef.current = null;
      }
    };
  }, [map, counts, allEnabled, enabledCountries, liveConfig, i18n.language, t]);

  // Click delegation for buttons
  useEffect(() => {
    const container = map.getContainer();
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'BUTTON' && target.id?.startsWith('nordic-req-')) {
        e.stopPropagation();
        const countryName = target.id.replace('nordic-req-', '');
        const label = COUNTRY_LABELS.find(c => c.country === countryName);
        if (label) {
          incrementCountryRequest(label.key).catch(() => {});
        }
        callbackRef.current?.(countryName);
      }
    };
    container.addEventListener('click', handleClick, true);
    return () => container.removeEventListener('click', handleClick, true);
  }, [map]);

  return null;
};

export default NordicComingSoonOverlay;
